import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, Trash2, CheckCircle2, AlertCircle, Clock, Paperclip, Download,
  Undo2, ShieldCheck, FileText, X, ChevronDown, Pencil, Copy, Save,
  Info, History, Wallet, FileStack, ReceiptText, ExternalLink, Package, TrendingUp, TrendingDown,
} from "lucide-react";
import IconAction from "@/components/app/IconAction";
import api, { fmtIDR, fmtDate } from "@/lib_app/api";
import { toast } from "sonner";
import NumberInput from "@/components/app/NumberInput";
import DateInput from "@/components/app/DateInput";
import Avatar from "@/components/app/Avatar";
import { useAuth } from "@/context/AuthContext";
import ProjectAttachmentsBoard from "@/components/app/ProjectAttachmentsBoard";
import { INVOICE_STATUS } from "@/components/app/InvoiceDetailModal";
import { PO_STATUS } from "@/components/app/PoDetailModal";
import { useNavigate } from "react-router-dom";

const CATEGORY_LABEL = { pemerintah: "Pemerintah", bumn: "BUMN", swasta: "Swasta", partner: "Partner" };
const CATEGORY_TINT = {
  pemerintah: { bg: "bg-[#FDE2CF]", fg: "text-[#8C4A17]" },
  bumn: { bg: "bg-[#DDEBFB]", fg: "text-[#2F4B70]" },
  swasta: { bg: "bg-[#E6E2F4]", fg: "text-[#4A3B77]" },
  partner: { bg: "bg-[#E4F9EC]", fg: "text-[#3D6E4C]" },
};

function statusMeta({ status, overdue }) {
  if (status === "done") return {
    label: "Selesai",
    chip: "bg-[#E4F9EC] text-[#4A6D53] border-[#B7E7C7]",
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
    heroBg: "linear-gradient(135deg, rgba(180,240,205,0.55) 0%, rgba(215,247,225,0.35) 100%)",
    heroBorder: "rgba(120,200,150,0.45)",
  };
  if (overdue) return {
    label: "Overdue",
    chip: "bg-[#FDE7E9] text-[#CE6968] border-[#FFB3BF]",
    icon: <AlertCircle className="w-3.5 h-3.5" />,
    heroBg: "linear-gradient(135deg, rgba(255,193,203,0.55) 0%, rgba(255,222,228,0.35) 100%)",
    heroBorder: "rgba(255,140,155,0.45)",
  };
  return {
    label: "On Progress",
    chip: "bg-[#E5EEFA] text-[#3B4A63] border-[#B0C7EA]",
    icon: <Clock className="w-3.5 h-3.5" />,
    heroBg: "linear-gradient(135deg, rgba(190,210,240,0.55) 0%, rgba(232,240,254,0.35) 100%)",
    heroBorder: "rgba(150,175,220,0.45)",
  };
}

export default function ProjectDetailModal({ open, project: projectProp, users, initialTab, onClose, onChanged }) {
  const { user } = useAuth();
  const [project, setProject] = useState(projectProp);
  const [mode, setMode] = useState("read");
  const [tab, setTab] = useState("info");
  const [form, setForm] = useState({});
  const [payments, setPayments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [newPay, setNewPay] = useState({ amount: 0, paid_at: "", method: "", note: "", invoice_id: "" });
  const [invoiceData, setInvoiceData] = useState({ summary: null, invoices: [], attachments: [] });
  const [poDocCount, setPoDocCount] = useState(0);
  const [poList, setPoList] = useState([]);
  const navigate = useNavigate();
  const [payFile, setPayFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [payFormOpen, setPayFormOpen] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const fileRef = useRef(null);

  const canEditProject = user && (user.role === "owner" || user.role === "sales_director");
  const canEditRemark = canEditProject || (user && project?.sales_id === user.user_id);
  const canEdit = canEditRemark;
  const canSeeMargin = user && ["owner", "finance", "sales_director"].includes(user.role);

  const userMap = useMemo(() => {
    const m = {};
    (users || []).forEach((u) => { m[u.user_id] = u; });
    return m;
  }, [users]);

  const today = new Date().toISOString().slice(0, 10);

  const refreshProject = async () => {
    if (!projectProp?.id) return;
    try { const r = await api.get(`/projects/${projectProp.id}`); setProject(r.data); } catch {}
  };
  const refreshPayments = async () => {
    if (!project?.id) return;
    try { const r = await api.get(`/projects/${project.id}/payments`); setPayments(r.data || []); } catch { setPayments([]); }
  };
  const refreshActivities = async () => {
    if (!project?.id) return;
    try { const r = await api.get(`/projects/${project.id}/activity`); setActivities(r.data || []); } catch { setActivities([]); }
  };
  const refreshInvoices = async () => {
    if (!project?.id) return;
    try { const r = await api.get(`/projects/${project.id}/invoices`); setInvoiceData(r.data || { summary: null, invoices: [], attachments: [] }); }
    catch { setInvoiceData({ summary: null, invoices: [], attachments: [] }); }
  };
  const refreshPOs = async () => {
    if (!project?.id) return;
    try { const r = await api.get("/purchase-orders", { params: { project_id: project.id } }); setPoList(r.data || []); }
    catch { setPoList([]); }
  };

  useEffect(() => {
    if (!project?.id) return;
    Promise.all([
      api.get("/purchase-orders", { params: { project_id: project.id } }).then((r) => (r.data || []).length).catch(() => 0),
      api.get(`/projects/${project.id}/po-attachments`).then((r) => (r.data || []).length).catch(() => 0),
    ]).then(([a, b]) => setPoDocCount(a + b));
  }, [project?.id]);

  useEffect(() => { setProject(projectProp); }, [projectProp]);
  useEffect(() => {
    if (!open || !project) return;
    setForm({
      name: project.service || project.name || "",
      ppn_type: project.ppn_type || "non_ppn",
      deadline: project.deadline || "",
      status: project.status || "on_progress",
      remark: project.remark || "",
      contract_no: project.contract_no || "",
      deal_date: project.deal_date ? String(project.deal_date).slice(0, 10) : "",
    });
    setMode("read");
    setTab(initialTab || "info");
    refreshProject();
    refreshPayments();
    refreshActivities();
    refreshInvoices();
    refreshPOs();
    setNewPay({ amount: 0, paid_at: new Date().toISOString().slice(0, 10), method: "", note: "", invoice_id: "" });
    setPayFile(null);
    if (fileRef.current) fileRef.current.value = "";
  }, [open, project?.id, initialTab]);

  if (!project) return null;

  const totalPaid = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const baseValue = project.base_value || 0;
  const previewIsPpn = form.ppn_type === "ppn";
  const previewPpnAmount = previewIsPpn ? Math.round(baseValue * 0.11) : 0;
  const previewTotal = baseValue + previewPpnAmount;
  const outstanding = Math.max(0, previewTotal - totalPaid);
  const pct = previewTotal > 0 ? totalPaid * 100 / previewTotal : 0;
  const poCostTotal = poList.filter((po) => po.status !== "void").reduce((s, po) => s + (po.subtotal || 0), 0);
  const marginBase = baseValue - poCostTotal;
  const marginPct = baseValue > 0 ? (marginBase / baseValue) * 100 : 0;
  const currentStatus = mode === "edit" ? form.status : project.status;
  const overdue = project.deadline && project.deadline < today && currentStatus !== "done";
  const isDone = currentStatus === "done";
  const meta = statusMeta({ status: currentStatus, overdue });
  const category = project.client_category || null;
  const catTint = category ? (CATEGORY_TINT[category] || CATEGORY_TINT.swasta) : null;

  const saveDetail = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (canEditProject) {
        payload.name = form.name;
        payload.ppn_type = form.ppn_type;
        payload.deadline = form.deadline || null;
        payload.deal_date = form.deal_date || null;
        payload.status = form.status;
        if (form.ppn_type === "ppn") payload.ppn_rate = 0.11;
      }
      payload.remark = form.remark || "";
      payload.contract_no = (form.contract_no || "").trim();
      await api.patch(`/projects/${project.id}`, payload);
      toast.success("Berhasil disimpan");
      await refreshProject();
      await refreshActivities();
      onChanged?.();
      setMode("read");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal simpan"); }
    finally { setSaving(false); }
  };

  const cancelEdit = () => {
    setForm({
      name: project.service || project.name || "",
      ppn_type: project.ppn_type || "non_ppn",
      deadline: project.deadline || "",
      status: project.status || "on_progress",
      remark: project.remark || "",
      contract_no: project.contract_no || "",
      deal_date: project.deal_date ? String(project.deal_date).slice(0, 10) : "",
    });
    setMode("read");
  };

  const toggleStatus = async () => {
    if (!canEditProject) return;
    const next = isDone ? "on_progress" : "done";
    if (!isDone && !confirm(`Tandai proyek "${project.customer}" sebagai selesai?\n\nOutstanding: ${fmtIDR(outstanding)}`)) return;
    setBusy(true);
    try {
      await api.patch(`/projects/${project.id}`, { status: next });
      await refreshProject();
      await refreshActivities();
      toast.success(next === "done" ? "Proyek ditandai selesai" : "Proyek dibuka kembali");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  const validFileExt = (f) => {
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    return ["pdf", "png", "jpg", "jpeg", "webp", "doc", "docx", "xls", "xlsx"].includes(ext);
  };
  const pickFile = (f) => {
    if (!f) { setPayFile(null); return; }
    if (!validFileExt(f)) return toast.error("Ekstensi tidak diizinkan");
    if (f.size > 15 * 1024 * 1024) return toast.error("File > 15MB");
    setPayFile(f);
  };

  const addPayment = async () => {
    if (!newPay.amount || newPay.amount <= 0) return toast.error("Nominal harus > 0");
    if (!newPay.note?.trim()) return toast.error("Keterangan pembayaran wajib diisi");
    setUploading(true);
    try {
      const created = await api.post(`/projects/${project.id}/payments`, newPay);
      if (payFile && created.data?.id) {
        const fd = new FormData();
        fd.append("file", payFile);
        try {
          await api.post(`/projects/${project.id}/payments/${created.data.id}/attachment`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch { toast.error("Pembayaran tersimpan tapi bukti gagal diupload"); }
      }
      await refreshPayments();
      await refreshActivities();
      await refreshInvoices();
      setNewPay({ amount: 0, paid_at: new Date().toISOString().slice(0, 10), method: "", note: "", invoice_id: "" });
      setPayFile(null);
      setPayFormOpen(false);
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Pembayaran ditambahkan");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    finally { setUploading(false); }
  };

  const deletePayment = async (pid) => {
    if (!canEditProject) return;
    if (!confirm("Hapus pembayaran ini?")) return;
    try {
      await api.delete(`/projects/${project.id}/payments/${pid}`);
      await refreshPayments();
      await refreshActivities();
      await refreshInvoices();
      toast.success("Pembayaran dihapus");
      onChanged?.();
    } catch { toast.error("Gagal"); }
  };

  const deleteProject = async () => {
    if (!canEditProject) return;
    const msg = totalPaid > 0
      ? `Hapus proyek "${project.customer} — ${project.service}"?\n\n⚠ ${payments.length} pembayaran senilai ${fmtIDR(totalPaid)} akan ikut terhapus.\n📤 Prospek terkait akan otomatis dibuka kembali.`
      : `Hapus proyek "${project.customer} — ${project.service}"?\n\n📤 Prospek terkait akan otomatis dibuka kembali.`;
    if (!confirm(msg)) return;
    setBusy(true);
    try {
      await api.delete(`/projects/${project.id}`);
      toast.success("Proyek dihapus");
      onChanged?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal hapus proyek"); }
    finally { setBusy(false); }
  };

  const downloadAttachment = (pid) => {
    const token = localStorage.getItem("sp_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/projects/${project.id}/payments/${pid}/attachment/download?auth=${encodeURIComponent(token || "")}`;
    window.open(url, "_blank");
  };

  const uploadProjectAttachments = async (fileList) => {
    if (!fileList || fileList.length === 0 || !project.prospect_id) return;
    const count = fileList.length;
    const fd = new FormData();
    Array.from(fileList).forEach((f) => fd.append("files", f));
    setAttaching(true);
    try {
      await api.post(`/prospects/${project.prospect_id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${count} lampiran diupload`);
      await refreshProject();
      await refreshActivities();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal upload"); }
    finally { setAttaching(false); }
  };

  const openProspectAttachment = (attId) => {
    if (!project.prospect_id) return;
    const token = localStorage.getItem("sp_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/prospects/${project.prospect_id}/attachments/${attId}/download?auth=${encodeURIComponent(token || "")}`;
    window.open(url, "_blank");
  };

  const deleteProspectAttachment = async (att, e) => {
    e?.stopPropagation();
    if (!canEditRemark || !project.prospect_id) return;
    if (!confirm(`Hapus lampiran "${att.filename}"?`)) return;
    try {
      await api.delete(`/prospects/${project.prospect_id}/attachments/${att.id}`);
      toast.success("Lampiran dihapus");
      await refreshProject();
      await refreshActivities();
      onChanged?.();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Gagal hapus"); }
  };

  const copyContract = () => {
    const cno = project.contract_no || "";
    if (!cno) return;
    navigator.clipboard?.writeText(cno);
    toast.success("Nomor kontrak disalin");
  };

  const uploadInvoiceDocs = async (invoiceId, category, files) => {
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    fd.append("category", category);
    try {
      await api.post(`/invoices/${invoiceId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshInvoices();
      toast.success("Dokumen invoice diupload");
    } catch (e) { toast.error(e.response?.data?.detail || e.response?.data?.message || "Gagal upload dokumen"); }
  };

  const deleteInvoiceDoc = async (att) => {
    if (!confirm(`Hapus dokumen "${att.filename}"?`)) return;
    try {
      await api.delete(`/invoices/${att.invoice_id}/attachments/${att.id}`);
      await refreshInvoices();
      toast.success("Dokumen dihapus");
    } catch { toast.error("Gagal hapus dokumen"); }
  };

  const openInvoiceDoc = (att) => {
    const token = localStorage.getItem("sp_token");
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${att.invoice_id}/attachments/${att.id}/download?auth=${encodeURIComponent(token || "")}`, "_blank");
  };

  const openInvoice = (invId) => { onClose(); navigate(`/invoices?open=${invId}`); };
  const openPO = (poId) => { onClose(); navigate(`/purchase-orders?open=${poId}`); };

  const tabs = [
    { key: "info", label: "Informasi", icon: Info },
    { key: "invoice", label: "Invoice", icon: ReceiptText, count: invoiceData.invoices.filter((i) => i.status !== "void").length },
    ...(canSeeMargin ? [{ key: "po", label: "Purchase Order", icon: Package, count: poList.length }] : []),
    { key: "lampiran", label: "Lampiran", icon: FileStack, count: (project.prospect_attachments?.length || 0) + (project.quotations_count || 0) + invoiceData.attachments.length + poDocCount },
    { key: "pembayaran", label: "Pembayaran", icon: Wallet, count: payments.length },
    { key: "riwayat", label: "Riwayat", icon: History },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-5xl max-h-[92vh] overflow-y-auto rounded-3xl border-0 p-0"
        data-testid="project-detail-modal"
        style={{
          background: "linear-gradient(160deg, rgba(255,255,255,0.96) 0%, rgba(232,240,254,0.92) 100%)",
          boxShadow: "0 32px 80px -20px rgba(59,74,99,0.30)",
        }}
      >
        <DialogTitle className="sr-only">Detail Proyek {project.customer}</DialogTitle>
        <DialogDescription className="sr-only">Informasi lengkap proyek {project.customer}</DialogDescription>

        <div className="p-6 space-y-5">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#6B8FE2] font-bold">
            Detail Proyek{mode === "edit" ? " · Mode Edit" : ""}
          </p>

          {/* HERO */}
          <div
            className="rounded-2xl p-5 backdrop-blur-xl"
            style={{
              background: meta.heroBg,
              border: `1px solid ${meta.heroBorder}`,
              boxShadow: "0 10px 30px -12px rgba(20,22,31,0.12), inset 0 1px 0 rgba(255,255,255,0.55)",
            }}
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className={`inline-flex items-center gap-1 h-6 px-2.5 rounded-full text-[10px] font-bold border ${meta.chip}`}>
                    {meta.icon} {meta.label}
                  </span>
                  {catTint && (
                    <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold ${catTint.bg} ${catTint.fg}`}>
                      {CATEGORY_LABEL[category]}
                    </span>
                  )}
                </div>
                <h2 className="text-2xl font-bold text-[#090808] leading-tight truncate">{project.customer}</h2>
                <p className="text-sm text-[#626061] mt-0.5 truncate">{project.service}</p>
              </div>
              {project.contract_no && (
                <button
                  onClick={copyContract}
                  className="inline-flex items-center gap-1.5 text-xs text-[#3B4A63] font-num tabular-nums font-semibold bg-white/70 hover:bg-white rounded-full px-3 h-8 border border-white/80 transition shrink-0 shadow-sm"
                  title="Klik untuk salin"
                  data-testid="project-contract-copy"
                >
                  <span className="text-[9px] uppercase tracking-widest text-[#A0A3A8]">NO</span>
                  {project.contract_no}
                  <Copy className="w-3 h-3 opacity-60" />
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
              <div className="flex items-center gap-2">
                <Avatar name={project.sales_name} picture={project.sales_picture} size="sm" className="!w-7 !h-7 !text-[11px]" />
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Sales</div>
                  <div className="text-sm font-semibold text-[#090808]">{project.sales_nickname || "-"}</div>
                </div>
              </div>
              <div className="w-px h-8 bg-white/70 hidden sm:block" />
              <div>
                <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Deal Date</div>
                <div className="text-sm font-semibold text-[#090808] font-num tabular-nums">{project.deal_date ? fmtDate(project.deal_date) : "-"}</div>
              </div>
              <div className="w-px h-8 bg-white/70 hidden sm:block" />
              <div>
                <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Deadline</div>
                <div className={`text-sm font-semibold font-num tabular-nums ${overdue ? "text-[#CE6968]" : "text-[#090808]"}`}>
                  {project.deadline ? fmtDate(project.deadline) : "—"}
                </div>
              </div>
              {catTint && (
                <>
                  <div className="w-px h-8 bg-white/70 hidden sm:block" />
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Kategori Klien</div>
                    <div className="text-sm font-semibold text-[#090808]">{CATEGORY_LABEL[category]}</div>
                  </div>
                </>
              )}
              {canEditProject && (
                <>
                  <div className="flex-1" />
                  <Button
                    onClick={toggleStatus}
                    disabled={busy}
                    data-testid="project-toggle-status-btn"
                    className={`h-9 rounded-full gap-1.5 px-4 font-semibold shadow-sm text-xs ${
                      isDone
                        ? "bg-white/80 text-[#3B4A63] hover:bg-white border border-[#B0C7EA]"
                        : "bg-gradient-to-r from-[#57D28A] to-[#3EB876] hover:from-[#3EB876] hover:to-[#2FA365] text-white"
                    }`}
                  >
                    {isDone ? <><Undo2 className="w-3.5 h-3.5" /> Buka Kembali</> : <><ShieldCheck className="w-3.5 h-3.5" /> Tandai Selesai</>}
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* STAT STRIP */}
          <div className="grid grid-cols-4 rounded-2xl bg-white/70 border border-white/80 backdrop-blur-xl overflow-hidden" style={{ boxShadow: "0 6px 18px rgba(59,74,99,0.08)" }}>
            <div className="p-4 border-r border-[#DCE0E7]/60">
              <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Nilai Dasar</div>
              <div className="text-base font-bold text-[#090808] tabular-nums font-num mt-1" data-testid="project-base-value">{fmtIDR(baseValue)}</div>
            </div>
            <div className="p-4 border-r border-[#DCE0E7]/60">
              <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">PPN</div>
              {previewIsPpn ? (
                <>
                  <div className="text-base font-bold text-[#3B4A63] tabular-nums font-num mt-1" data-testid="project-ppn-amount">{fmtIDR(previewPpnAmount)}</div>
                  <div className="text-[9px] text-[#6B8FE2] font-semibold">PPN 11%</div>
                </>
              ) : (
                <div className="text-sm font-semibold text-[#A0A3A8] mt-1" data-testid="project-ppn-amount">Non-PPN</div>
              )}
            </div>
            <div className="p-4 border-r border-[#DCE0E7]/60">
              <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Total</div>
              <div className="text-base font-bold text-[#090808] tabular-nums font-num mt-1" data-testid="project-total-value">{fmtIDR(previewTotal)}</div>
            </div>
            <div className="p-4">
              <div className="text-[9px] uppercase tracking-widest text-[#CE6968] font-bold">Outstanding</div>
              <div className="text-base font-bold text-[#CE6968] tabular-nums font-num mt-1" data-testid="project-outstanding">{fmtIDR(outstanding)}</div>
            </div>
          </div>

          {/* PROGRESS */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Progress Pembayaran</span>
              <span className="text-[10px] text-[#626061] font-semibold font-num tabular-nums">
                {fmtIDR(totalPaid)} / {fmtIDR(previewTotal)} · <span data-testid="project-paid-pct">{pct.toFixed(2)}%</span>
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-white/70 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#57D28A] to-[#3EB876] transition-all" style={{ width: `${Math.min(100, pct)}%` }} />
            </div>
          </div>

          {/* TAB BAR */}
          <div className="flex items-center justify-between border-b border-[#DCE0E7] gap-3">
            <div className="flex items-center overflow-x-auto flex-1">
              {tabs.map((t) => {
                const Ico = t.icon;
                const active = tab === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    data-testid={`project-tab-${t.key}`}
                    className={`inline-flex items-center gap-1.5 px-4 py-3 text-sm transition-colors whitespace-nowrap border-b-2 ${
                      active
                        ? (mode === "edit" ? "text-[#3B4A63] border-[#3B4A63] font-bold" : "text-[#090808] border-[#090808] font-bold")
                        : "text-[#A0A3A8] border-transparent hover:text-[#626061]"
                    }`}
                  >
                    <Ico className="w-3.5 h-3.5" />
                    {t.label}
                    {typeof t.count === "number" && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${active ? "bg-[#090808] text-white" : "bg-[#F4F5F7] text-[#626061]"}`}>
                        {t.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2 pb-2">
              {mode === "read" && canEdit && (
                <IconAction icon={Pencil} label="Edit" variant="info" size="sm" onClick={() => setMode("edit")} testid="project-edit-toggle" />
              )}
              {mode === "edit" && (
                <>
                  <IconAction icon={X} label="Batal" size="sm" onClick={cancelEdit} disabled={saving} testid="project-edit-cancel" />
                  <IconAction icon={Save} label={saving ? "Menyimpan…" : "Simpan Perubahan"} variant="primary" size="sm" onClick={saveDetail} disabled={saving} testid="project-save-btn" />
                </>
              )}
            </div>
          </div>

          {/* TAB CONTENT */}
          {tab === "info" && (
            <TabInformasi
              project={project} form={form} setForm={setForm}
              mode={mode} canEditProject={canEditProject} canEditRemark={canEditRemark}
              statusChip={meta} overdue={overdue}
            />
          )}
          {tab === "invoice" && (
            <TabInvoice data={invoiceData} onOpenInvoice={openInvoice} />
          )}
          {tab === "po" && canSeeMargin && (
            <TabPurchaseOrder poList={poList} baseValue={baseValue} poCostTotal={poCostTotal} marginBase={marginBase} marginPct={marginPct} onOpenPO={openPO} />
          )}
          {tab === "lampiran" && (
            <ProjectAttachmentsBoard
              project={project}
              invoices={invoiceData.invoices}
              invoiceAttachments={invoiceData.attachments}
              userMap={userMap}
              canEdit={canEditRemark}
              canDeleteInvoiceDoc={!!(user && (user.role === "owner" || user.role === "finance"))}
              attaching={attaching}
              onUploadProject={(files) => uploadProjectAttachments(files)}
              onOpenProject={openProspectAttachment}
              onDeleteProject={deleteProspectAttachment}
              onUploadInvoice={uploadInvoiceDocs}
              onOpenInvoiceDoc={openInvoiceDoc}
              onDeleteInvoiceDoc={deleteInvoiceDoc}
            />
          )}
          {tab === "pembayaran" && (
            <TabPembayaran
              payments={payments} userMap={userMap} totalPaid={totalPaid}
              previewTotal={previewTotal} outstanding={outstanding} pct={pct}
              canEditProject={canEditProject} onDelete={deletePayment} onDownload={downloadAttachment}
              payFormOpen={payFormOpen} setPayFormOpen={setPayFormOpen}
              newPay={newPay} setNewPay={setNewPay}
              payFile={payFile} setPayFile={setPayFile} pickFile={pickFile}
              addPayment={addPayment} uploading={uploading} fileRef={fileRef}
              invoices={invoiceData.invoices}
            />
          )}
          {tab === "riwayat" && <TabRiwayat activities={activities} />}

          {/* FOOTER */}
          <div className="pt-4 border-t border-[#E5EEFA]/60 flex flex-row items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {canEditProject && (
                <IconAction icon={Trash2} label="Hapus Proyek" variant="danger" onClick={deleteProject} disabled={busy} testid="project-delete-btn" />
              )}
            </div>
            <IconAction icon={X} label="Tutup" onClick={onClose} testid="project-modal-close" />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ==================== TAB: INFORMASI ==================== */
function TabInformasi({ project, form, setForm, mode, canEditProject, canEditRemark, statusChip, overdue }) {
  const isRead = mode === "read";

  if (isRead) {
    return (
      <div className="space-y-0 rounded-2xl bg-white/50 border border-[#E5EEFA]/60 px-5 pt-2 pb-4">
        <InfoRow label="Nama Proyek" value={project.service || project.name || "-"} />
        <InfoRow label="Tipe PPN" value={project.ppn_type === "ppn" ? "PPN 11%" : "Non-PPN"} />
        <InfoRow label="Nomor Kontrak / SPK" value={project.contract_no || <span className="text-[#A0A3A8]">Belum diisi</span>} valueClass={project.contract_no ? "font-num" : ""} />
        <InfoRow label="Deadline" value={project.deadline ? fmtDate(project.deadline) : <span className="text-[#A0A3A8]">Belum ditentukan</span>} valueClass="font-num" />
        <InfoRow
          label="Status"
          value={
            <span className="inline-flex items-center gap-2">
              <span className={`inline-flex items-center gap-1 h-6 px-2.5 rounded-full text-[10px] font-bold border ${statusChip.chip}`}>{statusChip.icon} {statusChip.label}</span>
              {overdue && <span className="text-[11px] text-[#CE6968] font-semibold">⚠ Deadline terlewat</span>}
            </span>
          }
        />
        <div className="pt-4">
          <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold mb-2">Remark / Catatan</div>
          {project.remark ? (
            <div className="rounded-xl bg-white/70 border border-[#DCE0E7]/70 p-3 text-sm text-[#3B4A63] whitespace-pre-wrap">{project.remark}</div>
          ) : (
            <div className="rounded-xl bg-[#F9FAFC] border border-[#DCE0E7]/70 p-3 text-sm text-[#A0A3A8] italic">Belum ada catatan.</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border-2 border-[#B0C7EA] bg-[#F5F9FF]/50 p-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Nama Proyek</Label>
          <Input data-testid="project-name-input" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={!canEditProject} className="rounded-xl bg-white h-11 mt-1" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Tipe PPN</Label>
          <Select value={form.ppn_type} onValueChange={(v) => setForm({ ...form, ppn_type: v })} disabled={!canEditProject}>
            <SelectTrigger data-testid="project-ppn-type" className="rounded-xl bg-white h-11 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ppn">PPN 11%</SelectItem>
              <SelectItem value="non_ppn">Non-PPN</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Deal Date</Label>
          <DateInput data-testid="project-dealdate-input" value={form.deal_date || ""} onChange={(e) => setForm({ ...form, deal_date: e.target.value })} disabled={!canEditProject} className="rounded-xl bg-white h-11" wrapperClassName="mt-1" />
          <div className="text-[10px] text-[#A0A3A8] mt-1">Menentukan bulan pencapaian di laporan &amp; target</div>
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Deadline</Label>
          <DateInput data-testid="project-deadline-input" value={form.deadline || ""} onChange={(e) => setForm({ ...form, deadline: e.target.value })} disabled={!canEditProject} className="rounded-xl bg-white h-11" wrapperClassName="mt-1" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Status</Label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })} disabled={!canEditProject}>
            <SelectTrigger data-testid="project-status-select" className="rounded-xl bg-white h-11 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="on_progress">On Progress</SelectItem>
              <SelectItem value="done">Selesai</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Nomor Kontrak / SPK</Label>
          <Input
            data-testid="project-contract-input" value={form.contract_no || ""}
            onChange={(e) => setForm({ ...form, contract_no: e.target.value })}
            placeholder="Contoh: SPK-2026-08-014"
            disabled={!canEditRemark}
            className="rounded-xl bg-white h-11 font-num mt-1"
          />
          <div className="text-[10px] text-[#A0A3A8] mt-1">Juga muncul di halaman Klien</div>
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Remark / Catatan</Label>
          <Textarea data-testid="project-remark-input" value={form.remark || ""} onChange={(e) => setForm({ ...form, remark: e.target.value })} rows={3} disabled={!canEditRemark} placeholder={canEditRemark ? "Tulis catatan / progress terkini proyek…" : ""} className="rounded-xl bg-white mt-1" />
        </div>
      </div>
      {!canEditProject && (
        <div className="mt-3 flex items-center gap-2 text-[11px] text-[#A0A3A8]">
          <span>🔒</span>
          <span>Field yang dikunci (Nilai Dasar, Nama Customer) tetap read-only untuk role Sales.</span>
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value, valueClass = "" }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-[#F4F5F7] last:border-0 gap-4">
      <span className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold shrink-0">{label}</span>
      <span className={`text-sm text-[#090808] font-semibold text-right ${valueClass}`}>{value}</span>
    </div>
  );
}

/* ==================== TAB: INVOICE ==================== */
function TabInvoice({ data, onOpenInvoice }) {
  const s = data.summary;
  const rows = data.invoices || [];
  const billedPct = s?.project_base_value > 0 ? Math.min(100, (s.billed_amount / s.project_base_value) * 100) : 0;

  return (
    <div className="space-y-4" data-testid="project-tab-invoice-content">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-2xl bg-[#E5EEFA] border border-[#B0C7EA] p-4">
          <div className="text-[9px] uppercase tracking-widest text-[#3B4A63] font-bold">Nilai Proyek</div>
          <div className="text-base font-bold text-[#3B4A63] mt-0.5 font-num tabular-nums">{fmtIDR(s?.project_base_value || 0)}</div>
          <div className="text-[10px] text-[#3B4A63] mt-0.5">sebelum PPN</div>
        </div>
        <div className="rounded-2xl bg-white border border-[#DCE0E7] p-4">
          <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Sudah Diinvoice</div>
          <div className="text-base font-bold text-[#090808] mt-0.5 font-num tabular-nums" data-testid="project-invoiced-amount">{fmtIDR(s?.billed_amount || 0)}</div>
          <div className="text-[10px] text-[#A0A3A8] mt-0.5 font-num tabular-nums">{(s?.billed_percent || 0).toFixed(2)}% · {s?.invoice_count || 0} invoice</div>
        </div>
        <div className="rounded-2xl bg-[#FFF3DA] border border-[#F2D9A0] p-4">
          <div className="text-[9px] uppercase tracking-widest text-[#8C6318] font-bold">Belum Diinvoice</div>
          <div className="text-base font-bold text-[#8C6318] mt-0.5 font-num tabular-nums" data-testid="project-not-invoiced">{fmtIDR(Math.max(0, s?.not_invoiced_amount || 0))}</div>
          <div className="text-[10px] text-[#8C6318] mt-0.5">nilai proyek tersisa</div>
        </div>
        <div className="rounded-2xl bg-[#E4F9EC] border border-[#B7E7C7] p-4">
          <div className="text-[9px] uppercase tracking-widest text-[#4A6D53] font-bold">Terbayar / Belum</div>
          <div className="text-base font-bold text-[#3EB876] mt-0.5 font-num tabular-nums">{fmtIDR(s?.paid_amount || 0)}</div>
          <div className="text-[10px] text-[#8E4747] mt-0.5 font-num tabular-nums">sisa {fmtIDR(s?.unpaid_amount || 0)}</div>
        </div>
      </div>

      <div className="h-1.5 rounded-full bg-white overflow-hidden border border-[#DCE0E7]/60">
        <div className="h-full bg-gradient-to-r from-[#6B8FE2] to-[#3B4A63] transition-all" style={{ width: `${billedPct}%` }} />
      </div>

      {rows.length === 0 ? (
        <div className="rounded-2xl bg-white/50 border border-[#DCE0E7]/60 p-8 text-center text-sm text-[#A0A3A8]" data-testid="project-invoice-empty">
          Proyek ini belum pernah diinvoice.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((inv) => {
            const st = INVOICE_STATUS[inv.status] || INVOICE_STATUS.draft;
            const unpaid = Math.max(0, inv.grand_total - inv.paid_amount);
            return (
              <div key={inv.id} onClick={() => onOpenInvoice(inv.id)} data-testid={`project-invoice-row-${inv.id}`}
                className="rounded-2xl bg-white border border-[#DCE0E7]/60 p-4 flex items-center gap-4 hover:shadow-md transition cursor-pointer">
                <div className="w-10 h-10 rounded-xl bg-[#E5EEFA] text-[#3B4A63] flex items-center justify-center shrink-0">
                  <ReceiptText className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] font-bold text-[#090808] font-num tabular-nums">{inv.number}</span>
                    <span className={`inline-flex items-center h-5 px-2 rounded-full text-[9px] font-bold border ${st.chip}`}>{st.label}</span>
                    <span className={`inline-flex items-center h-5 px-2 rounded-full text-[9px] font-bold uppercase tracking-widest ${inv.ppn_type === "ppn" ? "bg-[#D8EEDF] text-[#3f7a52]" : "bg-[#DCE0E7] text-[#626061]"}`}>
                      {inv.ppn_type === "ppn" ? "PPN 11%" : "Non-PPN"}
                    </span>
                    {inv.termin_enabled && (
                      <span className="text-[10px] font-semibold text-[#3B4A63]">{(inv.termin_label || "Termin")} {inv.termin_percent}%</span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#A0A3A8] mt-1 flex items-center gap-2 flex-wrap font-num tabular-nums">
                    <span>{inv.date ? fmtDate(inv.date) : "-"}</span>
                    <span>· tempo {inv.due_date ? fmtDate(inv.due_date) : "-"}</span>
                    {inv.attachments_count > 0 && (
                      <span className="inline-flex items-center gap-1 text-[#3B4A63]"><Paperclip className="w-3 h-3" />{inv.attachments_count} dokumen</span>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-bold text-[#090808] font-num tabular-nums">{fmtIDR(inv.grand_total)}</div>
                  <div className="text-[10px] font-num tabular-nums mt-0.5">
                    <span className="text-[#3EB876] font-semibold">{fmtIDR(inv.paid_amount)}</span>
                    <span className="text-[#A0A3A8]"> · sisa </span>
                    <span className={unpaid > 0 ? "text-[#CE6968] font-semibold" : "text-[#A0A3A8]"}>{fmtIDR(unpaid)}</span>
                  </div>
                </div>
                <ExternalLink className="w-4 h-4 text-[#A0A3A8] shrink-0" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ==================== TAB: PURCHASE ORDER (margin) ==================== */
function TabPurchaseOrder({ poList, baseValue, poCostTotal, marginBase, marginPct, onOpenPO }) {
  const rows = poList || [];
  const isProfit = marginBase >= 0;
  return (
    <div className="space-y-4" data-testid="project-tab-po-content">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="rounded-2xl bg-[#E5EEFA] border border-[#B0C7EA] p-4">
          <div className="text-[9px] uppercase tracking-widest text-[#3B4A63] font-bold">Revenue (Nilai Proyek)</div>
          <div className="text-base font-bold text-[#3B4A63] mt-0.5 font-num tabular-nums" data-testid="project-margin-revenue">{fmtIDR(baseValue)}</div>
          <div className="text-[10px] text-[#3B4A63] mt-0.5">sebelum PPN</div>
        </div>
        <div className="rounded-2xl bg-[#FDE7E9] border border-[#FFB3BF] p-4">
          <div className="text-[9px] uppercase tracking-widest text-[#8E4747] font-bold">Cost (Total PO Vendor)</div>
          <div className="text-base font-bold text-[#CE6968] mt-0.5 font-num tabular-nums" data-testid="project-margin-cost">{fmtIDR(poCostTotal)}</div>
          <div className="text-[10px] text-[#8E4747] mt-0.5 font-num tabular-nums">{rows.filter((po) => po.status !== "void").length} PO (termasuk draft)</div>
        </div>
        <div className={`rounded-2xl border p-4 ${isProfit ? "bg-[#E4F9EC] border-[#B7E7C7]" : "bg-[#FDE7E9] border-[#FFB3BF]"}`}>
          <div className={`text-[9px] uppercase tracking-widest font-bold ${isProfit ? "text-[#4A6D53]" : "text-[#8E4747]"}`}>Margin</div>
          <div className={`text-base font-bold mt-0.5 font-num tabular-nums flex items-center gap-1 ${isProfit ? "text-[#3EB876]" : "text-[#CE6968]"}`} data-testid="project-margin-value">
            {isProfit ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />} {fmtIDR(marginBase)}
          </div>
          <div className={`text-[10px] mt-0.5 font-num tabular-nums ${isProfit ? "text-[#4A6D53]" : "text-[#8E4747]"}`} data-testid="project-margin-pct">{marginPct.toFixed(2)}%</div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-2xl bg-white/50 border border-[#DCE0E7]/60 p-8 text-center text-sm text-[#A0A3A8]" data-testid="project-po-empty">
          Belum ada PO vendor yang terhubung ke proyek ini.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((po) => {
            const st = PO_STATUS[po.status] || PO_STATUS.draft;
            return (
              <div key={po.id} onClick={() => onOpenPO(po.id)} data-testid={`project-po-row-${po.id}`}
                className="rounded-2xl bg-white border border-[#DCE0E7]/60 p-4 flex items-center gap-4 hover:shadow-md transition cursor-pointer">
                <div className="w-10 h-10 rounded-xl bg-[#FDE7E9] text-[#CE6968] flex items-center justify-center shrink-0">
                  <Package className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] font-bold text-[#090808] font-num tabular-nums">{po.number}</span>
                    <span className={`inline-flex items-center h-5 px-2 rounded-full text-[9px] font-bold border ${st.chip}`}>{st.label}</span>
                  </div>
                  <div className="text-[11px] text-[#A0A3A8] mt-1 truncate">{po.vendor_name} · {po.date ? fmtDate(po.date) : "-"}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-bold text-[#090808] font-num tabular-nums">{fmtIDR(po.grand_total)}</div>
                  <div className="text-[10px] text-[#A0A3A8] mt-0.5 font-num tabular-nums">subtotal {fmtIDR(po.subtotal)}</div>
                </div>
                <ExternalLink className="w-4 h-4 text-[#A0A3A8] shrink-0" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ==================== TAB: PEMBAYARAN ==================== */
function TabPembayaran({ payments, userMap, totalPaid, previewTotal, outstanding, pct, canEditProject, onDelete, onDownload, payFormOpen, setPayFormOpen, newPay, setNewPay, payFile, setPayFile, pickFile, addPayment, uploading, fileRef, invoices = [] }) {
  const [collapsed, setCollapsed] = useState({});
  const toggleGroup = (key) => setCollapsed((c) => ({ ...c, [key]: !c[key] }));

  const groups = useMemo(() => {
    const invMap = {};
    invoices.forEach((i) => { invMap[i.id] = i; });
    const map = new Map();
    payments.forEach((p) => {
      const key = p.invoice_id || "__none__";
      if (!map.has(key)) map.set(key, { key, invoice: p.invoice_id ? invMap[p.invoice_id] || null : null, number: p.invoice_number || null, items: [] });
      map.get(key).items.push(p);
    });
    const arr = Array.from(map.values());
    arr.forEach((g) => {
      g.items.sort((a, b) => String(a.paid_at || "").localeCompare(String(b.paid_at || "")) || String(a.id).localeCompare(String(b.id)));
      g.total = g.items.reduce((s, p) => s + (p.amount || 0), 0);
    });
    arr.sort((a, b) => {
      if (a.key === "__none__") return 1;
      if (b.key === "__none__") return -1;
      const da = a.invoice?.date || "", db = b.invoice?.date || "";
      return String(da).localeCompare(String(db)) || String(a.number || "").localeCompare(String(b.number || ""));
    });
    return arr;
  }, [payments, invoices]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-2xl bg-[#E4F9EC] border border-[#B7E7C7] p-4">
          <div className="text-[9px] uppercase tracking-widest text-[#4A6D53] font-bold">Terbayar</div>
          <div className="text-lg font-bold text-[#3EB876] mt-0.5 tabular-nums font-num">{fmtIDR(totalPaid)}</div>
          <div className="text-[10px] text-[#4A6D53] mt-0.5 font-num tabular-nums">{pct.toFixed(2)}% dari total</div>
        </div>
        <div className="rounded-2xl bg-[#FDE7E9] border border-[#FFB3BF] p-4">
          <div className="text-[9px] uppercase tracking-widest text-[#8E4747] font-bold">Outstanding</div>
          <div className="text-lg font-bold text-[#CE6968] mt-0.5 tabular-nums font-num">{fmtIDR(outstanding)}</div>
          <div className="text-[10px] text-[#8E4747] mt-0.5 font-num tabular-nums">{Math.max(0, 100 - pct).toFixed(2)}% sisa</div>
        </div>
        <div className="rounded-2xl bg-[#E5EEFA] border border-[#B0C7EA] p-4">
          <div className="text-[9px] uppercase tracking-widest text-[#3B4A63] font-bold">Total Kontrak</div>
          <div className="text-lg font-bold text-[#3B4A63] mt-0.5 tabular-nums font-num">{fmtIDR(previewTotal)}</div>
          <div className="text-[10px] text-[#3B4A63] mt-0.5">{payments.length} pembayaran tercatat</div>
        </div>
      </div>

      <div className="rounded-2xl overflow-hidden bg-white/60 border border-[#B0C7EA]/60">
        <button
          type="button" onClick={() => setPayFormOpen((v) => !v)} data-testid="pay-form-toggle"
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/80 transition-colors"
        >
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#6B8FE2] to-[#3B4A63] text-white flex items-center justify-center">
              <Plus className="w-4 h-4" />
            </div>
            <div className="text-sm font-bold text-[#090808]">Tambah Pembayaran</div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-[#6B8FE2] font-semibold hidden sm:inline">Sisa: <span className="font-num tabular-nums">{fmtIDR(outstanding)}</span></span>
            <ChevronDown className={`w-4 h-4 text-[#3B4A63] transition-transform ${payFormOpen ? "rotate-180" : ""}`} />
          </div>
        </button>
        {payFormOpen && (
          <div className="px-4 pb-4 pt-1 space-y-3" data-testid="pay-form-panel">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Nominal *</Label>
                <NumberInput data-testid="pay-amount-input" value={newPay.amount} onChange={(v) => setNewPay({ ...newPay, amount: v })} className="bg-white rounded-xl h-11 mt-1" />
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Tanggal</Label>
                <DateInput data-testid="pay-date-input" value={newPay.paid_at} onChange={(e) => setNewPay({ ...newPay, paid_at: e.target.value })} className="bg-white rounded-xl h-11" wrapperClassName="mt-1" />
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Metode</Label>
                <Input data-testid="pay-method-input" value={newPay.method} onChange={(e) => setNewPay({ ...newPay, method: e.target.value })} placeholder="Transfer BCA, Tunai, Giro…" className="bg-white rounded-xl h-11 mt-1" />
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Keterangan *</Label>
                <Input data-testid="pay-note-input" value={newPay.note} onChange={(e) => setNewPay({ ...newPay, note: e.target.value })} placeholder="DP, Termijn 2, Pelunasan…" className="bg-white rounded-xl h-11 mt-1" />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Untuk Invoice</Label>
                <Select value={newPay.invoice_id || "none"} onValueChange={(v) => setNewPay({ ...newPay, invoice_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="pay-invoice-select" className="bg-white rounded-xl h-11 mt-1 border-[#DCE0E7]"><SelectValue placeholder="Pilih invoice" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Tanpa invoice (pembayaran proyek)</SelectItem>
                    {invoices.filter((i) => i.status !== "void" && i.status !== "draft").map((i) => (
                      <SelectItem key={i.id} value={i.id}>
                        <span className="font-num text-xs">{i.number}</span>
                        <span className="text-[10px] text-[#A0A3A8] ml-2">sisa {fmtIDR(Math.max(0, i.grand_total - i.paid_amount))}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-[10px] text-[#A0A3A8] mt-1">Status invoice otomatis jadi Dibayar Sebagian / Lunas.</div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx" onChange={(e) => pickFile(e.target.files?.[0])} className="hidden" data-testid="pay-file-input" />
              <Button type="button" onClick={() => fileRef.current?.click()} variant="outline" className="h-9 rounded-full gap-1.5 border-[#B0C7EA] text-[#3B4A63] hover:bg-[#E5EEFA]" data-testid="pay-file-pick-btn">
                <Paperclip className="w-3.5 h-3.5" /> {payFile ? "Ganti Bukti" : "Lampirkan Bukti"}
              </Button>
              {payFile && (
                <div className="inline-flex items-center gap-1.5 text-[11px] text-[#3B4A63] bg-white rounded-full px-3 h-8 border border-[#B0C7EA]" data-testid="pay-file-name">
                  <FileText className="w-3 h-3" /> {payFile.name}
                  <button type="button" onClick={() => { setPayFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-[#CE6968] ml-1">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              <div className="flex-1" />
              <Button onClick={addPayment} disabled={uploading} data-testid="pay-add-btn" className="gap-1.5 h-9 rounded-full bg-gradient-to-r from-[#6B8FE2] to-[#3B4A63] hover:from-[#5A7ED0] hover:to-[#2C3B54] text-white px-5 font-semibold">
                <Plus className="w-3.5 h-3.5" /> {uploading ? "Menyimpan…" : "Tambah"}
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3">
        {payments.length === 0 ? (
          <div className="rounded-2xl bg-white/50 border border-[#DCE0E7]/60 p-8 text-center text-sm text-[#A0A3A8]">
            Belum ada pembayaran tercatat.
          </div>
        ) : (
          groups.map((g) => {
            const inv = g.invoice;
            const st = inv ? (INVOICE_STATUS[inv.status] || INVOICE_STATUS.draft) : null;
            const invTotal = inv ? inv.grand_total : null;
            const invPaid = inv ? inv.paid_amount : g.total;
            const invUnpaid = inv ? Math.max(0, inv.grand_total - inv.paid_amount) : 0;
            const isNone = g.key === "__none__";
            const open = !collapsed[g.key];
            return (
              <div key={g.key} data-testid={`payment-group-${isNone ? "no-invoice" : g.key}`}
                className={`rounded-2xl overflow-hidden border ${isNone ? "border-[#DCE0E7]" : "border-[#B0C7EA]/70"} bg-white`}>
                <button type="button" onClick={() => toggleGroup(g.key)} data-testid={`payment-group-toggle-${isNone ? "no-invoice" : g.key}`}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 ${isNone ? "bg-[#F4F5F7]" : "bg-[#E5EEFA]"} hover:brightness-[0.98] transition`}>
                  <div className={`w-9 h-9 rounded-xl shrink-0 flex items-center justify-center ${isNone ? "bg-white text-[#626061] border border-[#DCE0E7]" : "bg-white text-[#3B4A63] border border-[#B0C7EA]"}`}>
                    {isNone ? <Wallet className="w-4 h-4" /> : <ReceiptText className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[13px] font-bold text-[#090808] font-num tabular-nums">
                        {isNone ? "Pembayaran Proyek (Tanpa Invoice)" : (g.number || "Invoice")}
                      </span>
                      {st && <span className={`inline-flex items-center h-5 px-2 rounded-full text-[9px] font-bold border ${st.chip}`}>{st.label}</span>}
                      {inv?.termin_enabled && (
                        <span className="inline-flex items-center h-5 px-2 rounded-full text-[9px] font-bold bg-white text-[#3B4A63] border border-[#B0C7EA]">
                          {(inv.termin_label || "Termin")} {inv.termin_percent}%
                        </span>
                      )}
                      <span className="text-[10px] text-[#3B4A63] font-semibold">{g.items.length} pembayaran</span>
                    </div>
                    <div className="text-[10px] text-[#3B4A63]/80 mt-0.5 font-num tabular-nums flex items-center gap-1.5 flex-wrap">
                      {inv ? (
                        <>
                          <span>Total {fmtIDR(invTotal)}</span>
                          <span>·</span>
                          <span className="text-[#3EB876] font-semibold">Terbayar {fmtIDR(invPaid)}</span>
                          <span>·</span>
                          <span className={invUnpaid > 0 ? "text-[#CE6968] font-semibold" : ""}>Sisa {fmtIDR(invUnpaid)}</span>
                        </>
                      ) : (
                        <span className="text-[#3EB876] font-semibold">Terbayar {fmtIDR(g.total)}</span>
                      )}
                    </div>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-[#3B4A63] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>

                {open && (
                  <div className="divide-y divide-[#DCE0E7]/60">
                    {g.items.map((p, i) => {
                      const creator = userMap[p.created_by];
                      const att = p.attachment && typeof p.attachment === "object" ? p.attachment : null;
                      return (
                        <div key={p.id} data-testid={`payment-row-${p.id}`} className="px-4 py-3 flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#57D28A] to-[#3EB876] text-white flex items-center justify-center shrink-0">
                            <CheckCircle2 className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-semibold text-[#090808] truncate">{p.note}{p.method ? ` · ${p.method}` : ""}</div>
                            <div className="text-[11px] text-[#A0A3A8] mt-0.5 flex items-center gap-2 flex-wrap">
                              {creator && (
                                <span className="inline-flex items-center gap-1">
                                  <Avatar name={creator.name} picture={creator.picture} size="sm" className="!w-3.5 !h-3.5 !text-[8px] !border-0" />
                                  {creator.nickname || creator.name}
                                </span>
                              )}
                              <span className="font-num tabular-nums">{fmtDate(p.paid_at)}</span>
                              {att && (
                                <button onClick={() => onDownload(p.id)} data-testid={`payment-attachment-${p.id}`} className="inline-flex items-center gap-1 text-[#6B8FE2] hover:underline">
                                  <Paperclip className="w-3 h-3" /> bukti
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-[15px] font-bold text-[#090808] tabular-nums font-num">{fmtIDR(p.amount)}</div>
                            <div className="text-[9px] text-[#3EB876] font-bold uppercase tracking-widest">Pembayaran {i + 1}</div>
                          </div>
                          {canEditProject && (
                            <button onClick={() => onDelete(p.id)} data-testid={`payment-delete-${p.id}`} className="w-8 h-8 rounded-full hover:bg-[#FDE7E9] text-[#CE6968] flex items-center justify-center shrink-0">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/* ==================== TAB: RIWAYAT ==================== */
function TabRiwayat({ activities }) {
  if (!activities || activities.length === 0) {
    return (
      <div className="rounded-2xl bg-white/50 border border-[#DCE0E7]/60 p-8 text-center text-sm text-[#A0A3A8]">
        Belum ada aktivitas tercatat.
      </div>
    );
  }
  return (
    <ol className="relative border-l-2 border-[#DCE0E7] ml-3 space-y-5" data-testid="project-activity-list">
      {activities.map((a) => {
        const info = eventMeta(a.event);
        return (
          <li key={a.id} className="pl-6 relative" data-testid={`activity-${a.id}`}>
            <div className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full ${info.dot} border-4 border-white shadow-sm`} />
            <div className="text-[10px] text-[#A0A3A8] font-bold uppercase tracking-widest font-num tabular-nums">
              {fmtDateTime(a.created_at)}
            </div>
            <div className="text-sm font-semibold text-[#090808] mt-0.5">{info.title(a)}</div>
            <div className="text-xs text-[#626061] mt-0.5">
              {(a.actor_nickname || a.actor_name || "System")}
              {info.subtitle(a) ? ` · ${info.subtitle(a)}` : ""}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function fmtDateTime(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function eventMeta(event) {
  const map = {
    project_created: {
      dot: "bg-[#6B8FE2]",
      title: () => "Proyek dibuat dari prospek",
      subtitle: (a) => {
        const d = a.details || {};
        const parts = [];
        if (d.total_value) parts.push(fmtIDR(d.total_value));
        if (d.ppn_type === "ppn") parts.push("PPN 11%"); else if (d.ppn_type === "non_ppn") parts.push("Non-PPN");
        return parts.join(" · ");
      },
    },
    payment_added: {
      dot: "bg-[#57D28A]",
      title: (a) => `Pembayaran ditambahkan · ${fmtIDR(a.details?.amount || 0)}`,
      subtitle: (a) => `${a.details?.note || ""}${a.details?.method ? ` via ${a.details.method}` : ""}`,
    },
    payment_deleted: {
      dot: "bg-[#CE6968]",
      title: (a) => `Pembayaran dihapus · ${fmtIDR(a.details?.amount || 0)}`,
      subtitle: (a) => a.details?.note || "",
    },
    status_changed: {
      dot: "bg-[#B39548]",
      title: (a) => `Status diubah: ${a.details?.from || "-"} → ${a.details?.to || "-"}`,
      subtitle: () => "",
    },
    remark_changed: {
      dot: "bg-[#B39548]",
      title: () => "Remark diperbarui",
      subtitle: (a) => (a.details?.to ? `"${(a.details.to || "").slice(0, 80)}${(a.details.to || "").length > 80 ? "…" : ""}"` : "Dikosongkan"),
    },
    contract_changed: {
      dot: "bg-[#3B4A63]",
      title: (a) => `Nomor kontrak: ${a.details?.to || "(kosong)"}`,
      subtitle: (a) => (a.details?.from ? `sebelumnya: ${a.details.from}` : ""),
    },
    ppn_changed: {
      dot: "bg-[#3B4A63]",
      title: (a) => `Tipe PPN: ${a.details?.from === "ppn" ? "PPN 11%" : "Non-PPN"} → ${a.details?.to === "ppn" ? "PPN 11%" : "Non-PPN"}`,
      subtitle: () => "",
    },
    deadline_changed: {
      dot: "bg-[#3B4A63]",
      title: (a) => `Deadline: ${a.details?.to ? fmtDate(a.details.to) : "dikosongkan"}`,
      subtitle: (a) => (a.details?.from ? `sebelumnya: ${fmtDate(a.details.from)}` : ""),
    },
    name_changed: {
      dot: "bg-[#3B4A63]",
      title: (a) => `Nama proyek: "${a.details?.to || ""}"`,
      subtitle: (a) => (a.details?.from ? `sebelumnya: "${a.details.from}"` : ""),
    },
    attachment_uploaded: {
      dot: "bg-[#4A3B77]",
      title: (a) => `Lampiran diupload: ${a.details?.filename || ""}`,
      subtitle: (a) => (a.details?.size ? `${Math.round(a.details.size / 1024)} KB` : ""),
    },
    attachment_deleted: {
      dot: "bg-[#CE6968]",
      title: (a) => `Lampiran dihapus: ${a.details?.filename || ""}`,
      subtitle: () => "",
    },
  };
  return map[event] || { dot: "bg-[#A0A3A8]", title: () => event, subtitle: () => "" };
}
